'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Store } from 'lucide-react'
import { listingShowsVerified } from '@/lib/listing-badges'
import VerifiedCheckBadge from '@/components/ui/VerifiedCheckBadge'

type Listing = {
  listing_id: number
  name?: string | null
  slug?: string | null
  photo?: string | null
  user_type?: string | null
  city?: string | null
  state?: string | null
  enhanced?: boolean | null
  verified?: boolean | null
  membership_type?: string | null
  business_description_completed?: boolean | null
  products_completed?: boolean | null
  vendors_completed?: boolean | null
  selling_locations_completed?: boolean | null
  sales_channels_completed?: boolean | null
}

function getProfileSrc(listing: Listing) {
  if (listing?.user_type?.toLowerCase() === 'producer') {
    return listing?.photo || '/assets/icons/tractor.svg'
  }
  if (listing?.user_type?.toLowerCase() === 'market') {
    return listing?.photo || '/assets/icons/Market.svg'
  }
  return listing?.photo || '/assets/icons/tractor.svg'
}

function getListingHref(listing: Listing) {
  if (listing?.user_type?.toLowerCase() === 'market') return `/market/${listing?.slug}`
  return `/producer/${listing?.slug}`
}

export function IngredientLocalAvailability({
  ingredientName,
  listings = [],
  previewCount = 2,
  parentName,
  parentListings = [],
  childListings = [],
}: {
  ingredientName?: string | null
  listings?: Listing[]
  previewCount?: number
  // Growers of the parent product (e.g. Apples on a Jonathan Apples page) who don't list
  // this exact variety -- always shown after exact matches and labelled as such.
  parentName?: string | null
  parentListings?: Listing[]
  // On a parent page (e.g. Apples): growers who list only a variety of it.
  childListings?: Listing[]
}) {
  const [expanded, setExpanded] = useState(false)
  const name = ingredientName || 'this ingredient'
  const hasParentListings = !!parentName && parentListings.length > 0
  const hasChildListings = childListings.length > 0
  const hasListings = listings.length > 0 || hasParentListings || hasChildListings
  // Exact matches first, then parent-level growers (flagged via `isParent`).
  const rows = [
    ...listings.map((listing) => ({ listing, isParent: false, isChild: false })),
    ...(hasParentListings ? parentListings.map((listing) => ({ listing, isParent: true, isChild: false })) : []),
    ...childListings.map((listing) => ({ listing, isParent: false, isChild: true })),
  ]
  const visible = expanded ? rows : rows.slice(0, previewCount)
  const canExpand = rows.length > previewCount

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl bg-[#F3FAEB] p-4">
      <div className="flex items-center gap-2">
        <Store className="h-5 w-5 shrink-0 text-[#2E7D0F]" strokeWidth={2} />
        <h2 className="text-base font-semibold text-[#2E7D0F] sm:text-lg sm:leading-[22px]">
          Where to buy fresh {name} near you
        </h2>
      </div>

      {!hasListings ? (
        <p className="text-sm text-[#6B6B6B]">
          No farms or markets are listed for {name} yet. Check back soon.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map(({ listing, isParent, isChild }) => (
            <Link
              key={listing.listing_id}
              href={getListingHref(listing)}
              className="flex min-h-[72px] items-center gap-3 rounded-xl border border-[#E6E6E6] bg-white p-3 transition-shadow hover:shadow-sm"
            >
              <div className="relative h-12 w-12 shrink-0">
                <img
                  src={getProfileSrc(listing)}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover"
                />
                {listingShowsVerified(listing) && (
                  <VerifiedCheckBadge
                    size={16}
                    className="absolute -bottom-0.5 -right-0.5"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold text-[#202020]">
                  {listing.name}
                </p>
                {listing.city && (
                  <p className="truncate text-sm text-[#333333]">{listing.city}</p>
                )}
                {isParent && (
                  <p className="truncate text-xs text-[#6B6B6B]">
                    Grows {parentName} (varieties may include {name})
                  </p>
                )}
                {isChild && (
                  <p className="truncate text-xs text-[#6B6B6B]">Grows varieties of {name}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="self-start text-base font-semibold text-[#2E7D0F] hover:underline"
        >
          {expanded ? 'Show less' : 'Expand to view all →'}
        </button>
      )}
    </div>
  )
}
